import classNames from 'classnames'
import {
    lazy,
    memo,
    Suspense,
    useCallback,
    useEffect,
    useMemo,
    useState
} from 'react'
import {useDispatch, useSelector} from 'react-redux'
import {Link} from 'react-router-dom'
import os from '../../../data/selectOptions/os.json'
import {ReminderIcon} from '../../../icons'
import {routes} from '../../../routes'
import {getServerResourceMetrics, persistMetricBasedRulesets, setRulesets} from '../../../stores/server-monitoring'
import NavigationHelper from '../../../utils/navigation'
import {isNewFeatureEnabled} from '../../../utils/newFeature'
import {getRelevantMetricKeys} from '../../../utils/rulesetMetrics'
import {osIcons} from '../../../utils/osIcons'
import {SimpleBlueButton} from '../../core/button'
import Input from '../../core/input'
import ContentLoader from '../../core/loader'
import Modal from '../../core/modal'
import Tooltip from '../../core/tooltip'
import RulesetListView from '../rulesetIntervalForm/RulesetListView'
import ListSkeleton from '../rulesetIntervalForm/listView/ListSkeleton'
import RulesetForm from '../rulesetIntervalForm/rulesetForm'
import modalTypes from './modalTypes.json'
import Treemap from './serverCharts/Treemap'
import ConfigFileComponent from './serverConfig/ConfigFileComponent'
import {useNamespaceTranslations} from '../../../utils/hooks/useNamespacesTranslations'
import useRulesetUsageLimit from '../../../utils/hooks/useRulesetUsageLimit'

const ExpandedLineChart = lazy(() => import('./serverCharts/ExpandedLineChart'))

const FilterOptions = memo(({value, setValue, placeholder}) => (
    <div className="filter-options">
        <Input
            value={value}
            inputClassName="filter-input"
            type="number"
            placeholder={placeholder}
            name="top-processes"
            onChange={(e) => setValue(e.target.value)}
        />
    </div>
))

const Modals = ({
    isVisible,
    activeModal,
    setActiveModal,
    showBackButton,
    setShowBackButton,
    uiConfig = {},
    rulesetConfig = {},
    serverMetricsConfig = {}
}) => {
    const {
        isDarkMode,
        telemetryUrl,
        selectedOsOption,
        selectedMetricTypes,
        serverResource,
        isMetricsDataRetrieving,
        isResourceFetching
    } = uiConfig

    const {
        rulesetData,
        rulesetFormInitialValues,
        usageLimits,
        rulesetTypeOptions,
        isCronJobRuleset,
        onDeleteRuleset,
        onOpenRulesetList
    } = rulesetConfig

    const {
        selectedMetric,
        instructionModalContent
    } = serverMetricsConfig

    const dispatch = useDispatch()
    const [originalProcessesData, setOriginalProcessesData] = useState([])
    const [processedTypeData, setProcessedTypeData] = useState({})
    const [inputValue, setInputValue] = useState(10)
    const text = useNamespaceTranslations('server-monitoring')
    const serverMetrics = useNamespaceTranslations('server-metrics')?.server_metrics

    const isRulesetsFetching = useSelector((state) => state.server_monitoring.isRulesetsFetching)
    const isRulesetsDirty = useSelector((state) => state.server_monitoring.isRulesetsDirty)
    const isRulesetsSaving = useSelector((state) => state.server_monitoring.isRulesetsSaving)

    const {categories, commonMetricsInfo, groupedData, absoluteAdditionalMetrics} = useSelector((state) => {
        const monitoringData = selectedMetric?.value ? state.server_monitoring[selectedMetric.value] : {}
        return {
            categories: monitoringData?.categories || [],
            commonMetricsInfo: monitoringData?.commonMetricsInfo || [],
            groupedData: monitoringData?.groupedData || {},
            absoluteAdditionalMetrics: monitoringData?.absoluteAdditionalMetrics || []
        }
    })

    const closeModal = useCallback(() => {
        setActiveModal({type: null, metric_type: null, modal_id: null})
        setShowBackButton(false)
    }, [setActiveModal, setShowBackButton])

    const showModal = useCallback((type) => {
        setActiveModal((prev) => ({...prev, type, metric_type: selectedMetric?.value, ruleset: null}))
    }, [selectedMetric?.value, setActiveModal])

    const showEditModal = useCallback((item) => {
        setActiveModal((prev) => ({...prev, type: modalTypes.ruleset_form_edit, ruleset: item}))
    }, [setActiveModal])

    const transformData = useCallback((data, metricType) => {
        return data?.filter(item => metricType !== serverMetrics.processes_unified.value || item?.data?.[0] !== 0)
            .sort((a, b) => (b?.data?.[0] ?? 0) - (a?.data?.[0] ?? 0))
    }, [])

    const zoomModalData = useMemo(() => transformData(groupedData?.[activeModal.modal_id], selectedMetric?.value),
        [groupedData,
            activeModal.modal_id,
            selectedMetric?.value,
            transformData])

    const effectiveRulesetUsageLimit = useRulesetUsageLimit(usageLimits?.incident_ruleset, !isCronJobRuleset)

    const highlightedMetricKeys = useMemo(() => (
        activeModal?.modal_id ? getRelevantMetricKeys(activeModal.metric_type, activeModal.modal_id) : null
    ), [activeModal?.metric_type, activeModal?.modal_id])

    const modalId = useMemo(() => {
        if ([modalTypes.expanded_chart, modalTypes.zoom_chart].includes(activeModal?.type)) return 'expanded-charts-modal'
        if (activeModal?.type === modalTypes.instruction) return 'rulesets-modal'
        return ''
    }, [activeModal?.type])

    const IconComponent = osIcons?.[selectedOsOption?.icon]

    const modalTitle = useMemo(() => {
        switch (activeModal?.type) {
        case modalTypes.expanded_chart:
            return selectedMetric?.title
        case modalTypes.instruction:
            return (
                <div className={showBackButton ? 'd-flex flex-column align-items-center' : ''}>
                    <span className="d-flex align-items-center">
                        {text.instructions}
                        <Tooltip
                            content={selectedOsOption?.label || os.centos.label}
                            trigger="mouseenter"
                            placement="bottom"
                            noButton
                            interactive>
                            <IconComponent height={selectedOsOption?.height || 28} />
                        </Tooltip>
                    </span>
                    <span className={classNames('os-desc', {dark: isDarkMode})}>
                        {text.not_right_os_text}
                        {text.click} <Link to={NavigationHelper.getNavigationUrl(
                            NavigationHelper.getLocalizedPath(routes.update_server_monitoring.paths), ':id', serverResource?.id)}>{text.here}</Link> {text.to_update}
                    </span>
                </div>
            )
        case modalTypes.zoom_chart:
            return `${serverMetrics.processes_unified.charts?.[activeModal?.modal_id]?.label} (${zoomModalData?.[0]?.unit})`
        default:
            return 'Ruleset details'
        }
    }, [activeModal?.type, selectedMetric?.title, selectedOsOption, isDarkMode, serverResource?.id, zoomModalData])

    const modalDataProps = useMemo(() => ({
        selectedMetric,
        categories: selectedMetric?.value === serverMetrics.processes_unified.value ?
            processedTypeData.categories :
            categories,
        commonMetricsInfo,
        rawMetricSeries: selectedMetric?.value === serverMetrics.processes_unified.value ?
            processedTypeData.groupedData :
            groupedData[activeModal.modal_id],
        absoluteMetrics: absoluteAdditionalMetrics,
        isDarkMode,
        isResourceFetching,
        isMetricsDataRetrieving,
        serverResource,
        selectedOsOption,
        selectedMetricTypes
    }), [selectedMetric,
        processedTypeData,
        categories,
        commonMetricsInfo,
        groupedData,
        activeModal.modal_id,
        absoluteAdditionalMetrics,
        isDarkMode,
        isResourceFetching,
        isMetricsDataRetrieving,
        serverResource,
        selectedOsOption,
        selectedMetricTypes])

    const contentClass = classNames({
        'expanded-chart': activeModal?.type === modalTypes.expanded_chart,
        'overflow-auto instruction-modal': activeModal?.type !== modalTypes.expanded_chart,
        'instruction-modal-container': activeModal?.type === modalTypes.instruction
    })

    const hasBack = useMemo(() =>
        (showBackButton && [modalTypes.ruleset_list, modalTypes.instruction].includes(activeModal?.type)) ||
    [modalTypes.ruleset_form, modalTypes.ruleset_form_edit].includes(activeModal?.type), [activeModal?.type, showBackButton])

    const backtoPreviousModal = useCallback(() => {
        if ([modalTypes.ruleset_list, modalTypes.instruction].includes(activeModal?.type)) {
            showModal(modalTypes.expanded_chart)
        } else {
            showModal(modalTypes.ruleset_list)
        }
    }, [activeModal?.type, showModal])

    const handleRulesetsModal = useCallback(() => {
        if (isNewFeatureEnabled()) {
            onOpenRulesetList?.()
            showModal(modalTypes.ruleset_list)
            setShowBackButton(true)
        }
    }, [showModal, setShowBackButton, onOpenRulesetList])

    const fetchProcessedMetrics = useCallback(async() => {
        const {categories, groupedData} = await dispatch(getServerResourceMetrics(serverMetrics, true, null, selectedMetric?.value))
        setOriginalProcessesData(groupedData?.[activeModal.modal_id])
        setProcessedTypeData({categories, groupedData: groupedData?.[activeModal.modal_id]})
    }, [dispatch, selectedMetric?.value, activeModal.modal_id])

    const filterChartData = useCallback(() => {
        setProcessedTypeData((prev) => ({
            ...prev,
            groupedData: transformData(originalProcessesData)?.slice(0, inputValue)
        }))
    }, [originalProcessesData, inputValue])

    const resetChartData = useCallback(() => {
        setProcessedTypeData((prev) => ({
            ...prev,
            groupedData: originalProcessesData
        }))
    }, [originalProcessesData])

    useEffect(() => {
        if (activeModal.type === modalTypes.expanded_chart && selectedMetric?.value === serverMetrics.processes_unified.value) {
            fetchProcessedMetrics()
        }
    }, [activeModal.type, selectedMetric?.value, fetchProcessedMetrics])

    return (
        <Modal
            isOpen={isVisible}
            id={modalId}
            contentClassName={contentClass}
            closeModal={closeModal}
            hasBack={hasBack}
            backtoPreviousModal={backtoPreviousModal}
            title={modalTitle}
        >
            {activeModal?.type === modalTypes.ruleset_list && isNewFeatureEnabled() && (
                !isCronJobRuleset && isRulesetsFetching ? (
                    <ListSkeleton isDarkMode={isDarkMode} />
                ) : (
                    <RulesetListView
                        data={rulesetData}
                        rulesetUsageLimit={effectiveRulesetUsageLimit}
                        isCronJobRuleset={isCronJobRuleset}
                        showFormViewModal={() => showModal(modalTypes.ruleset_form)}
                        showEditModal={showEditModal}
                        onSaveRulesets={!isCronJobRuleset ? () => dispatch(persistMetricBasedRulesets(serverResource)) : null}
                        isSaveDisabled={!isRulesetsDirty}
                        isSaving={isRulesetsSaving}
                        onDeleteRuleset={onDeleteRuleset}
                        highlightedMetricKeys={highlightedMetricKeys}
                    />
                )
            )}

            {activeModal?.type === modalTypes.instruction && (
                <ConfigFileComponent
                    url={telemetryUrl}
                    data={instructionModalContent}
                    language={selectedOsOption?.type || os.centos.type}
                    selectedMetricTypes={selectedMetricTypes}
                />
            )}

            {[modalTypes.ruleset_form, modalTypes.ruleset_form_edit].includes(activeModal?.type) && (
                <RulesetForm
                    rulesetFormInitialValues={rulesetFormInitialValues}
                    rulesetTypeOptions={rulesetTypeOptions}
                    metricOptions={rulesetFormInitialValues?.type?.options}
                    existingComponent={activeModal?.ruleset?.component?.value}
                    existingRuleset={activeModal?.ruleset}
                    setRulesetValues={(data) => dispatch(setRulesets(data))}
                    closeModal={() => showModal(modalTypes.ruleset_list)}
                    onCancel={() => showModal(modalTypes.ruleset_list)}
                    isCronJobRuleset={isCronJobRuleset}
                />
            )}

            {activeModal?.type === modalTypes.expanded_chart && (
                <div className={classNames('expanded-charts', {dark: isDarkMode})}>
                    <span className="chart-subtitle">
                        <span className="coming-soon-text" onClick={handleRulesetsModal}>
                            {text.set_rulesets}
                            <ReminderIcon />
                        </span>
                        {selectedMetric?.value === serverMetrics.processes_unified.value && (
                            <>
                                <FilterOptions value={inputValue} setValue={setInputValue} placeholder={text.top_processes}/>
                                <SimpleBlueButton onClick={filterChartData}>{text.filter}</SimpleBlueButton>
                                <SimpleBlueButton onClick={resetChartData} outline>{text.reset}</SimpleBlueButton>
                            </>
                        )}
                    </span>
                    <Suspense fallback={<ContentLoader />}>
                        <ExpandedLineChart {...modalDataProps} activeModal={activeModal} />
                    </Suspense>
                </div>
            )}

            {activeModal?.type === modalTypes.zoom_chart && (
                <Treemap
                    selectedMetric={selectedMetric}
                    isInModal
                    data={zoomModalData}
                    commonMetricsInfo={commonMetricsInfo}
                    isDarkMode={isDarkMode}
                    serverResource={serverResource}
                />
            )}
        </Modal>
    )
}

export default memo(Modals)
